"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import { PLAYGROUND_LANGUAGE_LINKS } from "@/features/playground/config/language-links";
import { formatCohortDate } from "@/features/home/lib/homepage-format";
import { formatBdt, freeLabel, fromPriceLabel, type CardPrice } from "@/features/courses/lib/card-price";
import { IntentLink } from "./IntentLink";
import { daysUntil, loadNextCohort, type NextCohort } from "./next-cohort";
import { startFreeHref, type MoreGroup, type NavEntry } from "./nav-model";
import styles from "./site-header.module.css";

/** One line of the panel and of the phone menu: the icon tile, the name, the line that says what it is. */
export function useEntryText() {
  const t = useTranslations("nav");
  return (entry: NavEntry) => ({
    label: t(entry.label),
    description: t(entry.description, { count: PLAYGROUND_LANGUAGE_LINKS.length }),
  });
}

/**
 * Small capitals for an eyebrow in English only: Bangla has no case, and
 * uppercasing a Bangla line turns its English words into shouting ("EXAM").
 */
export function eyebrowCase(locale: Locale): string {
  return locale === "bn" ? "" : "uppercase";
}

function priceText(price: CardPrice, locale: Locale): string {
  switch (price.kind) {
    case "free":
      return freeLabel(locale);
    case "from":
      return fromPriceLabel(price.amount, locale);
    default:
      return formatBdt(price.amount);
  }
}

/**
 * The card beside the groups: the next live cohort from the course record, or,
 * while no cohort is ahead (or the read failed), the free way in.
 */
function CohortCard({ locale, signedIn, onNavigate }: { locale: Locale; signedIn: boolean; onNavigate: () => void }) {
  const t = useTranslations("nav");
  // `undefined` while the read is in flight, `null` when there is no cohort ahead.
  const [cohort, setCohort] = useState<NextCohort | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void loadNextCohort().then((next) => {
      if (live) setCohort(next);
    });
    return () => {
      live = false;
    };
  }, []);

  // A strip under the groups from 1024, a column beside them from `xl`.
  const frame =
    "flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border-t-[3px] border-primary bg-neutral-900 px-5 py-4 text-white xl:flex-col xl:flex-nowrap xl:items-stretch xl:gap-3 xl:p-5";
  const cta = "ml-auto h-10 shrink-0 font-semibold xl:ml-0 xl:mt-auto xl:self-start";

  if (cohort === undefined) {
    return (
      <div className={frame} aria-busy="true">
        <span className="h-3 w-28 animate-pulse rounded bg-white/15" />
        <span className="h-5 w-4/5 animate-pulse rounded bg-white/15" />
        <span className="h-4 w-3/5 animate-pulse rounded bg-white/10" />
        <span className="ml-auto h-10 w-36 animate-pulse rounded-[10px] bg-white/10 xl:ml-0 xl:mt-auto" />
      </div>
    );
  }

  if (cohort === null) {
    return (
      <div className={frame}>
        <p className={cn("text-xs font-bold tracking-[0.12em] text-accent-peach", eyebrowCase(locale))}>{t("cohort.freeHeading")}</p>
        <p className="text-base font-bold leading-snug xl:text-xl">{t("cohort.freeTitle")}</p>
        <p className="text-sm leading-relaxed text-neutral-300">{t("cohort.freeBody")}</p>
        {signedIn ? null : (
          <Button asChild className={cta}>
            <IntentLink href={startFreeHref(locale)} onClick={onNavigate}>
              {t("startFree")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </IntentLink>
          </Button>
        )}
      </div>
    );
  }

  const title = locale === "bn" && cohort.title_bn ? cohort.title_bn : cohort.title_en;
  const days = daysUntil(cohort.startAt);

  return (
    <div className={frame}>
      <p className={cn("text-xs font-bold tracking-[0.12em] text-accent-peach", eyebrowCase(locale))}>{t("cohort.heading")}</p>
      <p className="text-base font-bold leading-snug xl:text-xl">{title}</p>
      <p className="flex items-center gap-2 text-sm text-neutral-300">
        <CalendarDays className="h-4 w-4 shrink-0 text-accent-peach" aria-hidden />
        {t("cohort.starts", { date: formatCohortDate(cohort.startAt, locale) })}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex h-[26px] items-center rounded-full bg-primary/20 px-2.5 text-[13px] font-bold text-accent-peach">
          {t("cohort.inDays", { days })}
        </span>
        <span className="text-[15px] font-bold">{priceText(cohort.price, locale)}</span>
      </div>
      <Button asChild className={cta}>
        <IntentLink href={`/${locale}/courses/${cohort.slug}`} onClick={onNavigate}>
          {t("cohort.cta")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </IntentLink>
      </Button>
    </div>
  );
}

function PanelItem({ entry, active, onNavigate }: { entry: NavEntry; active: boolean; onNavigate: () => void }) {
  const text = useEntryText()(entry);
  const Icon = entry.icon;
  return (
    <IntentLink
      href={entry.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group flex items-start gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors duration-150 ease-out hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
        active && "bg-primary/5",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary transition-colors duration-150 ease-out group-hover:bg-primary group-hover:text-white">
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-semibold leading-snug text-neutral-900">{text.label}</span>
        <span className="mt-0.5 hidden text-[13px] leading-normal text-neutral-500 xl:block">{text.description}</span>
      </span>
    </IntentLink>
  );
}

/**
 * `SITE-NAV01`: everything on Progsity on one screen, in the four groups the
 * canvas drew (Learn, Practice, Teach and assess, Progsity), with the next live
 * cohort beside them. From `xl` each link carries its one line; below it the
 * names alone, so the panel still fits a 1024 by 768 screen.
 */
export function MorePanel({
  id,
  groups,
  isActive,
  signedIn,
  onNavigate,
  onPointerEnter,
  onPointerLeave,
  footer,
}: {
  id: string;
  groups: MoreGroup[];
  isActive: (entry: NavEntry) => boolean;
  signedIn: boolean;
  onNavigate: () => void;
  onPointerEnter: (event: React.PointerEvent) => void;
  onPointerLeave: (event: React.PointerEvent) => void;
  footer: ReactNode;
}) {
  const t = useTranslations("nav");
  const locale = useLocale() as Locale;
  const caps = eyebrowCase(locale);

  return (
    <div
      id={id}
      role="region"
      aria-label={t("morePanelLabel")}
      className="absolute inset-x-0 top-full hidden pt-2 lg:block"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <Container>
        <div className={cn("overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-card", styles.drop)}>
          <div className="grid gap-3 p-4 xl:grid-cols-[minmax(0,1fr)_300px] xl:p-5">
            <div className="grid grid-cols-4 gap-2">
              {groups.map((group) => (
                <div key={group.id} className="flex min-w-0 flex-col gap-0.5">
                  <p className={cn("px-3 pb-2 text-xs font-bold tracking-[0.1em] text-neutral-400", caps)}>{t(group.heading)}</p>
                  {group.entries.map((entry) => (
                    <PanelItem key={entry.id} entry={entry} active={isActive(entry)} onNavigate={onNavigate} />
                  ))}
                </div>
              ))}
            </div>
            <div className="flex flex-col [&>*]:flex-1">
              <CohortCard locale={locale} signedIn={signedIn} onNavigate={onNavigate} />
            </div>
          </div>
          {footer}
        </div>
      </Container>
    </div>
  );
}
